<script>
import { cmsMenuProps, useCmsMenu } from "./mixin";
import CmsMenuLayer from "./cms-menu-layer/default";
import List from "@/components/molecules/list";
import ListItem from "@/components/molecules/list/list-item";
import Button from "@/components/atoms/button";

export default {
  name: "CmsMenu",
  components: {
    List,
    ListItem,
    Button,
    CmsMenuLayer
  },
  props: {
    ...cmsMenuProps()
  },
  computed: {
    routePath() {
      return useRoute().fullPath;
    }
  },
  watch: {
    routePath() {
      this.hide();
    }
  },
  setup() {
    const { activeCategory, showCategory, hide } = useCmsMenu();
    return {
      activeCategory,
      showCategory,
      hide
    };
  }
};
</script>

<template>
  <div class="c-cms-menu u-flex">
    <nav
      class="c-cms-menu__nav"
      :class="activeCategory && 'c-cms-menu__nav--opened'"
    >
      <List>
        <ListItem
          v-for="(item, index) in menu"
          :key="`cms-menu-${index}`"
          class="c-cms-menu__nav-item"
          :class="[activeCategory && activeCategory.attributes && item.attributes && item.attributes.libelle === activeCategory.attributes.libelle && 'c-cms-menu__nav-item--active' , item.divider && 'c-cms-menu__nav-item--divider', index === menu.length -1 && 'c-cms-menu__nav-item--divider']"
        >
          <template v-if="item.attributes && item.attributes.children && item.attributes.children.data && item.attributes.children.data.length>0">
            <Button
              class="c-cms-menu__nav-button has-child"
              @click="showCategory(item)"
            >
              {{ item.attributes.libelle }}
            </Button>
          </template>
          <template v-else>
            <Button
              :to="item.attributes.slug"
              class="c-cms-menu__nav-button"
              :class="[item.external && 'c-cms-menu__nav-button--external']"
            >
              {{ item.attributes.libelle }}
            </Button>
          </template>
        </ListItem>
        <ListItem class="c-cms-menu__nav-item">
          <Button
            to="/infos-et-services/aide/faq"
            class="c-cms-menu__nav-button"
          >
            Foire aux questions
          </Button>
        </ListItem>
        <ListItem class="c-cms-menu__nav-item c-cms-menu__nav-item--divider">
          <Button
            to="/infos-et-services/aide/contactez-nous"
            class="c-cms-menu__nav-button"
          >
            Contactez-nous
          </Button>
        </ListItem>

        <ListItem
          v-if="hasLocationCora"
          class="c-cms-menu__nav-item"
        >
          <Button
            :href="`https://${webAlias}.location.cora.fr`"
            target="_blank"
            class="c-cms-menu__nav-button c-cms-menu__nav-button--external"
          >
            Cora Location
          </Button>
        </ListItem>
        <ListItem class="c-cms-menu__nav-item">
          <Button
            href="https://www.coraphotos.fr"
            target="_blank"
            class="c-cms-menu__nav-button c-cms-menu__nav-button--external"
          >
            Photos
          </Button>
        </ListItem>
        <ListItem class="c-cms-menu__nav-item">
          <Button
            href="https://www.spectacles.carrefour.fr/fr"
            target="_blank"
            class="c-cms-menu__nav-button c-cms-menu__nav-button--external"
          >
            Carrefour Spectacles
          </Button>
        </ListItem>
        <ListItem class="c-cms-menu__nav-item">
          <Button
            href="https://www.prime-energie-cora.fr"
            target="_blank"
            class="c-cms-menu__nav-button c-cms-menu__nav-button--external"
          >
            Prime énergie
          </Button>
        </ListItem>
        <ListItem class="c-cms-menu__nav-item">
          <Button
            href="https://numerisation.carrefour.fr/fr/accueil"
            target="_blank"
            class="c-cms-menu__nav-button c-cms-menu__nav-button--external"
          >
            Numérisation
          </Button>
        </ListItem>
      </List>
    </nav>
    <div class="c-cms-menu__content">
      <div
        class="c-cms-menu__layer-container u-flex"
        @mouseleave="hide()"
      >
        <div
          v-if="activeCategory"
          class="c-cms-menu__layer"
        >
          <CmsMenuLayer :category="activeCategory" />
        </div>
      </div>
      <div
        v-if="activeCategory !== null"
        class="c-cms-menu__backdrop"
      />
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.c-cms-menu {
  height: 100%;

  .c-cms-menu__content {
    position: relative;
    width: 100%;
    z-index: 1;
    min-height: 70rem;
  }

  .c-cms-menu__layer {
    background-color: color(white);
    height: 100%;
    z-index: 4;
  }

  .c-cms-menu__layer-final {
    z-index: 3;
  }

  .c-cms-menu__layer-container {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    z-index: 3;
  }

  .c-cms-menu__nav {
    background-color: color(white);
    padding: spacing(sm) 0;
    width: 28rem;
    flex-shrink: 0;
    box-shadow: shadow(md);
  }

  .c-cms-menu__accordion {
    border-bottom: 1px solid color(grey-5);
    padding: spacing(sm) 0;
  }

  .c-cms-menu__backdrop {
    z-index: 2;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 33, 55, 0.8);
  }

  .c-cms-menu__nav--opened {
    box-shadow: 2px 0 6px rgba(0, 0, 0, 0.16);
  }

  .c-cms-menu__nav {
    padding-right: 1rem;
  }

  .c-cms-menu__nav-item {
    padding: 0.6rem 0;
  }

  .c-cms-menu__nav-button {
    justify-content: flex-start;
    width: 100%;
    padding: spacing(xs) spacing(sm);
    position: relative;
    text-align: left;
    font-size: 1.5rem;
    border-top-right-radius: radius(full);
    border-bottom-right-radius: radius(full);
    position: relative;
    &:hover,
    &:focus,
    .c-cms-menu__nav-item--active & {
      background-color: color(primary-lighter);
      color: color(primary-dark);
    }
    &.has-child {
      padding-right: 4rem;
      &:after {
        content: "";
        width: 2.4rem;
        height: 2.4rem;
        position: absolute;
        right: 1rem;
        top: 50%;
        transform: translateY(-50%);
        background-image: url("@/assets/icons/arrow-right.svg");
        background-position: center;
        background-repeat: no-repeat;
        background-size: auto 2.4rem;
      }

      &:hover,
      &:focus {
        &:after {
          filter: invert(16%) sepia(99%) saturate(2774%) hue-rotate(190deg)
            brightness(96%) contrast(97%);
          /*
          * Couleur : primary-dark;
          * Outil pour générer le filter : https://codepen.io/sosuke/pen/Pjoqqp
          */
        }
      }
    }
  }

  .c-cms-menu__nav-item--divider {
    position: relative;
    margin-bottom: 0.6rem;
    padding-bottom: 1.2rem;
    &:after {
      content: "";
      position: absolute;
      left: 1.6rem;
      right: 0;
      bottom: 0;
      background-color: transparent;
      background-image: radial-gradient(
        circle,
        color(grey-2) 0px,
        transparent 1px
      );
      background-size: 6px 4px;
      background-repeat: repeat-x;
      height: 3px;
    }
  }

  .c-cms-menu__nav-button--external {
    padding-right: 4rem;
    font-size: 1.5rem;
    color: color(text-light);
    padding-top: 1rem;
    padding-bottom: 1rem;
    &:after {
      width: 2.4rem;
      height: 2.4rem;
      position: absolute;
      right: 1rem;
      top: 50%;
      transform: translateY(-50%);
      background-image: url("@/assets/icons/external.svg");
      filter: invert(69%) sepia(97%) saturate(1807%) hue-rotate(176deg)
        brightness(92%) contrast(90%);
      /*
      * Couleur : primary-light;
      * Outil pour générer le filter : https://codepen.io/sosuke/pen/Pjoqqp
      */
      background-position: center;
      background-repeat: no-repeat;
      background-size: auto 2.4rem;
    }
    &:hover,
    &:focus {
      &:after {
        content: "";
      }
    }
  }
}
</style>
